// src/components/game/ui/ViralRecruiter.jsx
import React, { useState } from 'react';

// --- CONFIGURATION ---
const MISSIONS = {
  movement: {
    id: 'movement',
    title: "SIGNAL: THE MOVEMENT",
    desc: "Invite others to the 'Privileged Blue-Collar Club'. Free simulators, professional games, and partnership opportunities.",
    url: "https://aulice-hub.web.app", // Main URL
    copy: "Join the exquisite community of gamechangers. Free simulators, professional games, and access to world-changing projects. A privileged blue-collar club honoring those who build the future. Partner, play, and earn. It's a movement.",
    reward: "+100 Civilization Levels"
  },
  leaderlab: {
    id: 'leaderlab',
    title: "SIGNAL: THE ARCHITECT",
    desc: "Distribute 'The LeaderLab' blueprints. Awaken potential architects for the new reality.",
    url: "https://aulice-hub.web.app/leaderlab",
    copy: "I have discovered the source code for a new reality. Access the LeaderLab protocol here. Architects needed for world-changing projects.",
    reward: "Access to RESEARCH Sector"
  },
  games: {
    id: 'games',
    title: "SIGNAL: THE SIMULATION",
    desc: "Share the Professional Games & Simulators. Train the next generation of elites.",
    url: "https://aulice-hub.web.app/games",
    copy: "Test your cognitive firmware. Professional simulators and money-making partnerships available. Play to win in the new economy.",
    reward: "Unlock 'ELITE' Class"
  }
};

export default function ViralRecruiter({ onComplete, onCancel }) {
  const [activeMission, setActiveMission] = useState('movement');
  const [step, setStep] = useState(1); // 1: Select, 2: Share, 3: Verify
  const [proofFile, setProofFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");

  const mission = MISSIONS[activeMission];

  const handleShare = (platform) => {
    const text = encodeURIComponent(mission.copy);
    const url = encodeURIComponent(mission.url);
    let shareUrl = "";

    if (platform === 'twitter') {
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
    } 
    else if (platform === 'linkedin') {
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
    } 
    else if (platform === 'facebook') {
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    else if (platform === 'instagram') {
        // Instagram doesn't allow direct web sharing. 
        // Logic: Copy text to clipboard -> Open Instagram
        navigator.clipboard.writeText(`${mission.copy} ${mission.url}`);
        setCopyStatus("CAPTION COPIED!");
        setTimeout(() => {
            window.open('https://instagram.com', '_blank');
            setCopyStatus("");
        }, 1000);
    }

    setStep(3); // Move to verification
  };

  const handleVerify = () => {
    if (!proofFile) return alert("UPLOAD VISUAL CONFIRMATION.");
    
    setIsScanning(true);
    // Simulate AI Scan
    setTimeout(() => {
        setIsScanning(false);
        onComplete(mission.reward);
    }, 2500);
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 font-mono text-white overflow-y-auto">
      <div className="w-full max-w-5xl bg-slate-900 border-2 border-cyan-500 rounded p-1 shadow-[0_0_50px_rgba(6,182,212,0.2)] my-auto">
        
        {/* HEADER */}
        <div className="bg-cyan-900/20 p-4 border-b border-cyan-700 flex justify-between items-center">
          <h2 className="text-2xl text-cyan-400 font-bold tracking-widest">/// NETWORK UPLINK ///</h2>
          <button onClick={onCancel} className="text-red-500 hover:text-red-400 font-bold">[ABORT]</button>
        </div>

        <div className="p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* LEFT: MISSION SELECTOR */}
          <div className="space-y-4">
            <h3 className="text-cyan-600 text-sm uppercase mb-2 border-b border-gray-700 pb-1">Select Transmission Protocol</h3>
            {Object.values(MISSIONS).map(m => (
              <button
                key={m.id}
                onClick={() => { setActiveMission(m.id); setStep(1); }}
                className={`w-full text-left p-4 border transition-all rounded ${activeMission === m.id ? 'border-cyan-400 bg-cyan-900/30 text-white shadow-lg' : 'border-slate-700 text-slate-500 hover:border-cyan-700 hover:text-gray-300'}`}
              >
                <div className="font-bold tracking-wider">{m.title}</div>
                <div className="text-xs mt-1 text-green-400">REWARD: {m.reward}</div>
              </button>
            ))}
          </div>

          {/* RIGHT: ACTION PANEL */}
          <div className="border-l border-slate-700 pl-0 md:pl-8 flex flex-col justify-center">
            <h3 className="text-xl text-white mb-2 font-bold">{mission.title}</h3>
            <p className="text-sm text-gray-400 mb-6 italic">"{mission.desc}"</p>

            {step === 1 && (
                <div className="space-y-4 animate-fade-in">
                    <div className="p-4 bg-black border border-slate-600 text-cyan-300 text-xs font-mono leading-relaxed rounded relative">
                        <div className="absolute -top-3 left-2 bg-slate-900 px-2 text-gray-500 text-[10px]">TRANSMISSION_PREVIEW</div>
                        "{mission.copy} {mission.url}"
                    </div>
                    <button onClick={() => setStep(2)} className="w-full py-4 bg-cyan-600 hover:bg-cyan-500 text-black font-bold rounded text-lg shadow-[0_0_15px_rgba(34,211,238,0.4)] transition-all">
                        INITIATE BROADCAST
                    </button>
                </div>
            )}

            {step === 2 && (
                <div className="space-y-3 animate-fade-in">
                    <p className="text-xs text-cyan-300 uppercase tracking-widest">Select Network Frequency:</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => handleShare('twitter')} className="py-3 border border-slate-600 bg-black hover:bg-slate-800 hover:border-white transition-all rounded">
                            𝕏 / TWITTER
                        </button>
                        <button onClick={() => handleShare('facebook')} className="py-3 border border-blue-800 bg-blue-900/20 hover:bg-blue-900 hover:border-blue-500 transition-all rounded text-blue-200">
                            FACEBOOK
                        </button>
                        <button onClick={() => handleShare('linkedin')} className="py-3 border border-blue-600 bg-blue-800/20 hover:bg-blue-800 hover:border-blue-400 transition-all rounded text-blue-300">
                            LINKEDIN
                        </button>
                        <button onClick={() => handleShare('instagram')} className="py-3 border border-pink-700 bg-pink-900/20 hover:bg-pink-900 hover:border-pink-500 transition-all rounded text-pink-300 relative">
                            {copyStatus || "INSTAGRAM"}
                        </button>
                    </div>

                    <button onClick={() => setStep(3)} className="w-full py-2 text-xs text-gray-500 hover:text-white mt-4 underline decoration-gray-700 hover:decoration-white">
                        >> I have completed the broadcast (Skip to Verification)
                    </button>
                </div>
            )}

            {step === 3 && (
                <div className="space-y-4 animate-fade-in">
                    <div className="border-2 border-dashed border-slate-600 rounded h-40 flex flex-col items-center justify-center relative hover:border-cyan-500 transition bg-black/40 group cursor-pointer">
                        <input type="file" onChange={(e) => setProofFile(e.target.files[0])} className="absolute inset-0 opacity-0 cursor-pointer z-10" />
                        
                        {proofFile ? (
                            <div className="text-center">
                                <div className="text-4xl mb-2">📄</div>
                                <span className="text-green-400 font-bold">{proofFile.name}</span>
                                <div className="text-xs text-gray-500 mt-1">EVIDENCE LOADED</div>
                            </div>
                        ) : (
                            <>
                                <span className="text-4xl mb-2 grayscale group-hover:grayscale-0 transition-all">📸</span>
                                <span className="text-sm text-gray-400 group-hover:text-white font-bold">UPLOAD SCREENSHOT PROOF</span>
                                <span className="text-[10px] text-gray-600 mt-1">Click or Drag & Drop</span>
                            </>
                        )}
                    </div>
                    
                    <button 
                        onClick={handleVerify}
                        disabled={!proofFile || isScanning}
                        className={`w-full py-4 font-bold rounded text-lg transition-all ${isScanning ? 'bg-slate-700 text-slate-500 cursor-wait' : 'bg-green-600 hover:bg-green-500 text-white shadow-[0_0_20px_rgba(34,197,94,0.4)] animate-pulse'}`}
                    >
                        {isScanning ? "ANALYZING SIGNAL PATTERN..." : "VERIFY & UNLOCK"}
                    </button>
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}